import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Divider, 
  CircularProgress,
  Button,
  Card,
  CardMedia,
  IconButton,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { Calendar, User, Share2, Facebook, Twitter, Linkedin, ArrowLeft } from 'lucide-react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Importez cette constante ou créez un fichier de configuration
const CLOUDINARY_CLOUD_NAME = 'dhke5v8gz';
// URL du placeholder
const PLACEHOLDER_URL = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/v1745764847/agrivision_placeholder_cklmxu.jpg`;

// Fonction pour optimiser les images pour le partage social et l'affichage responsive
const getOptimizedImageUrl = (imageUrl, type = 'social') => {
  if (!imageUrl) return PLACEHOLDER_URL;
  
  // Si c'est déjà une URL Cloudinary, ajouter les transformations pour optimiser
  if (imageUrl.includes('cloudinary.com')) {
    // Différentes optimisations selon l'usage
    if (type === 'social') {
      // Format optimisé pour les réseaux sociaux (1200x630 est le format recommandé)
      return imageUrl.replace('/upload/', '/upload/c_fill,g_auto,w_1200,h_630,q_auto,f_auto/');
    } else if (type === 'thumbnail') {
      // Format miniature pour l'affichage dans l'article
      return imageUrl.replace('/upload/', '/upload/c_fill,g_auto,w_400,h_300,q_auto,f_auto/');
    } else if (type === 'whatsapp') {
      // Format spécifique pour WhatsApp (carré 400x400 pour meilleure visibilité)
      // Optimisé spécifiquement pour WhatsApp avec une meilleure qualité et un format carré
      return imageUrl.replace('/upload/', '/upload/c_fill,g_auto,w_400,h_400,q_85,f_auto,e_sharpen/');
    } else if (type === 'article') {
      // Format pour l'image principale de l'article - optimisé pour être responsive
      return imageUrl.replace('/upload/', '/upload/c_fill,g_auto,w_1600,h_900,q_auto,f_auto,dpr_auto/');
    } else if (type === 'article-mobile') {
      // Format optimisé pour mobile avec ratio différent
      return imageUrl.replace('/upload/', '/upload/c_fill,g_auto,w_800,h_600,q_auto,f_auto/');
    }
  }
  
  return imageUrl;
};

// Fonction pour générer une URL absolue (importante pour le partage)
const getAbsoluteUrl = (path) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  return `${siteUrl}${path}`;
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const articleRef = doc(db, 'news', articleId);
        const articleSnap = await getDoc(articleRef);
        
        if (articleSnap.exists()) {
          setArticle({
            id: articleSnap.id,
            ...articleSnap.data(),
            createdAt: articleSnap.data().createdAt?.toDate() || new Date()
          });
        } else {
          setError('Article non trouvé');
        }
      } catch (err) {
        console.error('Erreur lors du chargement de l\'article:', err);
        setError('Impossible de charger l\'article. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);

  // Fonction pour formater la date
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Fonction pour partager sur les réseaux sociaux
  // Utiliser l'URL complète avec le domaine pour le partage
  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const shareUrl = getAbsoluteUrl(`/news/${articleId}`);
  const shareTitle = article?.title || 'Article d\'Agri Vision Gabon';
  const shareText = article?.summary || article?.content?.substring(0, 100) || '';
  
  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  
  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  
  const shareOnWhatsApp = () => {
    // Pour WhatsApp, l'URL doit être en premier pour que la miniature s'affiche correctement
    // Le format est important: URL en premier, puis texte
    // Ajout d'un espace après l'URL pour améliorer la détection par WhatsApp
    const whatsappText = `${shareUrl} \n\n${shareTitle}\n\n${shareText}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, '_blank');
  };
  
  // Fonction pour copier le lien de l'article
  const copyArticleLink = () => {
    navigator.clipboard.writeText(shareUrl);
    // Ici vous pourriez ajouter une notification de succès si nécessaire
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress sx={{ color: '#014527' }} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error" align="center">
          {error || 'Article non trouvé'}
        </Typography>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/news')}
            startIcon={<ArrowLeft />}
            sx={{ 
              bgcolor: '#014527', 
              '&:hover': { bgcolor: '#01331d' } 
            }}
          >
            Retour aux actualités
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | Agri Vision Gabon</title>
        <meta name="description" content={article.summary || article.content.substring(0, 160)} />
        <link rel="canonical" href={shareUrl} />
        <link rel="image_src" href={getOptimizedImageUrl(article.imageUrl, 'whatsapp')} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.summary || article.content.substring(0, 160)} />
        <meta property="og:image" content={getOptimizedImageUrl(article.imageUrl, 'social')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Agri Vision Gabon" />
        <meta property="og:locale" content="fr_FR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.summary || article.content.substring(0, 160)} />
        <meta name="twitter:image" content={getOptimizedImageUrl(article.imageUrl, 'social')} />
        <meta name="twitter:site" content="@AgriVisionGabon" />
        
        {/* WhatsApp spécifique - ces balises sont importantes pour WhatsApp */}
        <meta property="og:image:alt" content={article.title} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:secure_url" content={getOptimizedImageUrl(article.imageUrl, 'whatsapp')} />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="400" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Balises supplémentaires pour améliorer la compatibilité */}
        <meta property="og:image:secure_url" content={getOptimizedImageUrl(article.imageUrl, 'social')} />
        <meta property="og:image:url" content={getOptimizedImageUrl(article.imageUrl, 'social')} />
        
        {/* Balises spécifiques pour WhatsApp - format carré pour meilleure visibilité */}
        <meta property="og:image:width:whatsapp" content="400" />
        <meta property="og:image:height:whatsapp" content="400" />
        <meta property="og:image:secure_url:whatsapp" content={getOptimizedImageUrl(article.imageUrl, 'whatsapp')} />
        <meta property="og:image:url:whatsapp" content={getOptimizedImageUrl(article.imageUrl, 'whatsapp')} />
      </Helmet>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Fil d'Ariane */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <MuiLink 
            underline="hover" 
            color="inherit" 
            href="/"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Accueil
          </MuiLink>
          <MuiLink 
            underline="hover" 
            color="inherit" 
            href="/news"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Actualités
          </MuiLink>
          <Typography color="text.primary">{article.title}</Typography>
        </Breadcrumbs>
        
        <Card sx={{ 
          mb: 4, 
          borderRadius: 2, 
          overflow: 'hidden',
          boxShadow: 'none',
          bgcolor: 'transparent'
        }}>
          {article.imageUrl && (
            <Box sx={{
              position: 'relative',
              mb: 4,
              borderRadius: { xs: 1, sm: 2 },
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.4s ease-in-out',
              '&:hover': {
                transform: 'scale(1.01)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
              },
              height: { xs: '250px', sm: '350px', md: '450px', lg: '550px' },
            }}>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, height: '100%', width: '100%' }}>
                <CardMedia
                  component="img"
                  image={getOptimizedImageUrl(article.imageUrl, 'article')}
                  alt={article.title}
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
              </Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' }, height: '100%', width: '100%' }}>
                <CardMedia
                  component="img"
                  image={getOptimizedImageUrl(article.imageUrl, 'article-mobile')}
                  alt={article.title}
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </Box>
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                height: '30%',
                display: { xs: 'none', sm: 'block' },
              }} />
            </Box>
          )}
          
          {/* Miniature cachée pour le partage sur WhatsApp - invisible pour l'utilisateur mais utile pour le partage */}
          <Box sx={{ display: 'none' }}>
            <img 
              src={getOptimizedImageUrl(article.imageUrl, 'whatsapp')} 
              alt={article.title} 
              width="400" 
              height="400" 
              property="og:image"
              itemProp="image"
            />
            {/* Image supplémentaire avec propriétés spécifiques pour WhatsApp */}
            <img 
              src={getOptimizedImageUrl(article.imageUrl, 'whatsapp')} 
              alt={`${article.title} - Agri Vision Gabon`} 
              width="400" 
              height="400" 
              property="og:image:whatsapp"
              itemProp="thumbnailUrl"
            />
          </Box>
          
          {/* Miniature de l'image (visible uniquement sur les écrans plus grands) */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            mb: 3,
            gap: 3
          }}>
            <Box sx={{
              width: '150px',
              height: '120px',
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              flexShrink: 0
            }}>
              <img 
                src={getOptimizedImageUrl(article.imageUrl, 'thumbnail')} 
                alt={article.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ 
              fontWeight: 'bold',
              color: '#014527',
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 0
            }}>
              {article.title}
            </Typography>
          </Box>
          
          {/* Version mobile avec miniature plus petite et titre */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              gap: 2
            }}>
              <Box sx={{
                width: '80px',
                height: '80px',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}>
                <img 
                  src={getOptimizedImageUrl(article.imageUrl, 'thumbnail')} 
                  alt={article.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography variant="h3" component="h1" gutterBottom sx={{ 
                fontWeight: 'bold',
                color: '#014527',
                fontSize: '1.5rem',
                mb: 0
              }}>
                {article.title}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center', 
            mb: 4, 
            color: 'text.secondary',
            borderBottom: '1px solid #e0e0e0',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
              <Calendar size={16} />
              <Typography variant="body2" sx={{ ml: 1, mr: 3 }}>
                {formatDate(article.createdAt)}
              </Typography>
              <User size={16} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {article.author || 'Agri Vision Gabon'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Partager:
              </Typography>
              <IconButton onClick={shareOnFacebook} size="small" aria-label="Partager sur Facebook">
                <Facebook size={18} />
              </IconButton>
              <IconButton onClick={shareOnTwitter} size="small" aria-label="Partager sur Twitter">
                <Twitter size={18} />
              </IconButton>
              <IconButton onClick={shareOnLinkedIn} size="small" aria-label="Partager sur LinkedIn">
                <Linkedin size={18} />
              </IconButton>
              <IconButton onClick={shareOnWhatsApp} size="small" aria-label="Partager sur WhatsApp" sx={{ color: '#25D366' }}>
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          {article.summary && (
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
              mb: 3,
              fontSize: '1.1rem',
              fontStyle: 'italic',
              color: '#333'
            }}>
              {article.summary}
            </Typography>
          )}
          
          <Typography variant="body1" component="div" sx={{ 
            whiteSpace: 'pre-line',
            lineHeight: 1.8,
            fontSize: '1.05rem',
            color: '#333',
            '& p': { mb: 2 }
          }}>
            {article.content}
          </Typography>
          
          {article.externalLink && (
            <Box sx={{ mt: 4 }}>
              <Button 
                variant="contained" 
                href={article.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  bgcolor: '#014527', 
                  '&:hover': { bgcolor: '#01331d' } 
                }}
              >
                {article.linkLabel || 'En savoir plus'}
              </Button>
            </Box>
          )}
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            mt: 6,
            pt: 3,
            borderTop: '1px solid #e0e0e0'
          }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/news')}
              startIcon={<ArrowLeft />}
              sx={{ 
                color: '#014527', 
                borderColor: '#014527',
                '&:hover': { 
                  borderColor: '#01331d',
                  backgroundColor: 'rgba(1, 69, 39, 0.05)' 
                } 
              }}
            >
              Retour aux actualités
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Partager:
              </Typography>
              <IconButton onClick={shareOnFacebook} size="small" color="primary" aria-label="Partager sur Facebook">
                <Facebook size={18} />
              </IconButton>
              <IconButton onClick={shareOnTwitter} size="small" color="primary" aria-label="Partager sur Twitter">
                <Twitter size={18} />
              </IconButton>
              <IconButton onClick={shareOnLinkedIn} size="small" color="primary" aria-label="Partager sur LinkedIn">
                <Linkedin size={18} />
              </IconButton>
              <IconButton onClick={shareOnWhatsApp} size="small" aria-label="Partager sur WhatsApp" sx={{ color: '#25D366' }}>
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Card>
      </Container>
    </>
  );
};

export default ArticlePage;